"use client";
import { pluralForm, type Locale, type PluralForms } from "@/lib/i18n/translations";
import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "@/lib/i18n/LanguageContext";
import { cn } from "@/lib/utils";
import { keepTogether } from "@/lib/keepTogether";

type Heading = { id: string; text: string };

const COPY = {
  de: { label: "Inhalt", aria: "Inhaltsverzeichnis", sections: "Abschnitte" },
  en: { label: "Contents", aria: "Table of contents", sections: "sections" },
  da: { label: "Indhold", aria: "Indholdsfortegnelse", sections: "afsnit" },
  ar: { label: "المحتويات", aria: "جدول المحتويات", sections: "أقسام" },
  uk: { label: "Зміст", aria: "Зміст статті", sections: "розділів" },
  ru: { label: "Содержание", aria: "Оглавление", sections: "разделов" },
  vi: { label: "Nội dung", aria: "Mục lục", sections: "mục" },
  es: { label: "Contenido", aria: "Índice", sections: "secciones" },
} as const;

/** "3 розділи", "5 розділів": languages whose plural is more than one/other. */
const SECTION_FORMS: Partial<Record<Locale, PluralForms>> = {
  uk: { one: "розділ", few: "розділи", many: "розділів", other: "розділу" },
  ru: { one: "раздел", few: "раздела", many: "разделов", other: "раздела" },
  ar: { one: "قسم", two: "قسمان", few: "أقسام", many: "قسمًا", other: "قسم" },
};

function useCopy() {
  const { locale } = useTranslation();
  return COPY[locale];
}

function clean(text: string) {
  return text.replace(/^\p{Emoji_Presentation}+\s*/u, "").trim();
}

/** Distance from the top of the viewport at which a heading counts as the
 *  current section: just below the sticky header. */
const ACTIVE_LINE = 160;

/**
 * Desktop sidebar table of contents. The current entry is the last heading
 * that has passed just below the header, measured on scroll: the former
 * IntersectionObserver band skipped headings on anchor jumps and long
 * scrolls, so "01" stayed highlighted deep inside section five.
 */
export function ArticleToc({ headings }: { headings: Heading[] }) {
  const c = useCopy();
  const [active, setActive] = useState<string>(headings[0]?.id ?? "");
  const listRef = useRef<HTMLOListElement>(null);

  // A long list scrolls inside its card: keep the current entry in view, so
  // the highlight never sits on a hidden line near the end of the article.
  useEffect(() => {
    const list = listRef.current;
    const item = list?.querySelector<HTMLElement>('[aria-current="location"]');
    if (!list || !item || list.scrollHeight <= list.clientHeight) return;
    const lr = list.getBoundingClientRect();
    const ir = item.getBoundingClientRect();
    if (ir.top < lr.top) list.scrollTop -= lr.top - ir.top + 8;
    else if (ir.bottom > lr.bottom) list.scrollTop += ir.bottom - lr.bottom + 8;
  }, [active]);

  useEffect(() => {
    if (headings.length < 2) return;
    let frame = 0;
    const update = () => {
      frame = 0;
      let current = headings[0].id;
      for (const { id } of headings) {
        const el = document.getElementById(id);
        if (!el) continue;
        if (el.getBoundingClientRect().top <= ACTIVE_LINE) current = id;
        else break;
      }
      setActive(current);
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav
      aria-label={c.aria}
      className="flex min-h-0 flex-col rounded-lg border border-navy-100 bg-white p-5 shadow-[var(--shadow-soft)]"
    >
      <p className="eyebrow mb-3">{c.label}</p>
      {/* Scrolls inside the card when it is long: the sidebar is never taller
          than the window (page.tsx), and this list is what gives way, so the
          check card under it always stays whole. */}
      <ol ref={listRef} className="toc-list min-h-0 flex-1 space-y-0.5 overflow-y-auto overscroll-contain pe-1">
        {headings.map(({ id, text }, i) => {
          const isActive = active === id;
          return (
            <li key={id}>
              <a
                href={`#${id}`}
                aria-current={isActive ? "location" : undefined}
                className={cn(
                  "flex items-start gap-2.5 rounded py-1.5 text-[13.5px] leading-snug transition-colors",
                  isActive ? "text-coral-700" : "text-navy-600 hover:text-ink-900",
                )}
              >
                <span
                  className={cn(
                    "mt-px shrink-0 font-display text-[12.5px] tabular-nums lining-nums",
                    isActive ? "text-coral-700" : "text-navy-400",
                  )}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span>{keepTogether(clean(text))}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/** Mobile table of contents as a collapsible `<details>` above the article body. */
export function ArticleTocMobile({ headings }: { headings: Heading[] }) {
  const c = useCopy();
  const { locale } = useTranslation();
  if (headings.length < 2) return null;

  return (
    <details className="group mb-8 overflow-hidden rounded-lg border border-navy-100 bg-white shadow-[var(--shadow-soft)] lg:hidden [&_summary]:list-none">
      <summary className="flex min-h-12 cursor-pointer items-center justify-between gap-3 px-5 py-3 transition-colors group-open:bg-cream-100">
        <span className="flex items-center gap-2.5">
          <span className="eyebrow">{c.label}</span>
          <span className="text-[13.5px] text-navy-500">
            · {headings.length} {SECTION_FORMS[locale] ? pluralForm(locale, headings.length, SECTION_FORMS[locale]!) : c.sections}
          </span>
        </span>
        <ChevronDown
          className="h-4 w-4 text-navy-500 transition-transform duration-200 group-open:rotate-180"
          strokeWidth={2}
          aria-hidden
        />
      </summary>
      <ol className="border-t border-navy-100 px-5 py-2">
        {headings.map(({ id, text }, i) => (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={(e) => {
                const details = e.currentTarget.closest("details") as HTMLDetailsElement | null;
                if (details) details.open = false;
              }}
              className="flex items-start gap-3 rounded py-2.5 text-[14.5px] leading-snug text-navy-700 transition-colors hover:text-coral-700"
            >
              <span className="mt-px shrink-0 font-display text-[13px] tabular-nums lining-nums text-navy-400">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span>{keepTogether(clean(text))}</span>
            </a>
          </li>
        ))}
      </ol>
    </details>
  );
}
